
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Home } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatZinc = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Zinc - Rachat Gouttières, Toitures, Profilés | Groupe Fabre"
        description="Rachat de zinc au meilleur prix : gouttières, toitures, profilés, bardages. Pesée certifiée et paiement immédiat."
        canonical="/nos-services/achat-metaux/zinc"
        keywords="achat zinc, rachat gouttières zinc, toiture zinc, profilés zinc, prix zinc, ferrailleur zinc"
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-slate-500 to-slate-600 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Home className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat de Zinc</h1>
                  <p className="text-xl mt-2 opacity-90">Rachat de gouttières, toitures et profilés</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de zinc - Matériau de couverture
                </h2>
                <p className="text-gray-600 mb-6">
                  Le <strong>zinc</strong> est un métal non ferreux largement utilisé dans la construction. 
                  <strong>Groupe Fabre Environnement</strong> rachète tous types de zinc : 
                  gouttières, couvertures, bardages et profilés de construction.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Types de zinc rachetés</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-slate-500 text-white mr-3">•</span>
                    <span><strong>Gouttières :</strong> demi-rondes, carrées, chéneaux</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-slate-500 text-white mr-3">•</span>
                    <span><strong>Toitures :</strong> feuilles, bacs, plaques ondulées</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-slate-500 text-white mr-3">•</span>
                    <span><strong>Profilés :</strong> cornières, barres, tubes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-slate-500 text-white mr-3">•</span>
                    <span><strong>Bardages :</strong> cassettes, plaques planes</span>
                  </li>
                </ul>

                <div className="bg-slate-50 border border-slate-200 p-6 rounded-lg">
                  <h4 className="font-bold text-slate-800 mb-3">Propriétés du zinc</h4>
                  <ul className="text-slate-700 text-sm space-y-2">
                    <li>• Résistance naturelle à la corrosion</li>
                    <li>• Recyclable à 100% sans perte de qualité</li>
                    <li>• Longévité exceptionnelle (plus de 80 ans)</li>
                    <li>• Facilité de mise en œuvre</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Valorisez votre zinc</h3>
                <p className="text-gray-600 mb-6">
                  Le zinc conserve une valeur élevée sur le marché du recyclage. 
                  Apportez vos chutes ou démontages zinc pour une valorisation optimale.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-slate-600 mr-3" />
                    <span>Prix attractifs zinc</span>
                  </div>
                  <div className="flex items-center">
                    <Home className="h-5 w-5 text-slate-600 mr-3" />
                    <span>Spécialiste matériaux BTP</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-slate-600 mr-3" />
                    <span>Tri par qualité de zinc</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-slate-600 text-white rounded-md font-medium hover:bg-slate-700 transition-colors">
                    Demander prix zinc
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Classifications zinc */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Classifications zinc et applications
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Zinc Naturel</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Zinc pur, patine naturelle gris clair
                </p>
                <div className="text-green-600 font-medium">Prix premium</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Zinc Prépatiné</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Zinc traité, aspect uniforme dès la pose
                </p>
                <div className="text-orange-600 font-medium">Prix standard</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Alliages Zinc</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Zinc-titane, zinc-cuivre pour rigidité
                </p>
                <div className="text-blue-600 font-medium">Prix selon alliage</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatZinc;
